import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Box, SelectChangeEvent, Snackbar, FormControlLabel, Checkbox, Card, CardContent, Grid, Divider } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { useActions } from "../../hooks/useActions";
import "../../assets/style.css";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import EncompassService from "../../services/encompassService";
import { IDeliveryType, IExpression } from "../../models/configuration/plugin/IConfiguration";
import { RootState, store } from "../../state/store";
import { useSelector } from "react-redux";
import DeliveryTypesList from "../../components/configuration/deliveryTypes/DeliveryTypesList";
import DeliveryTypeDetails from "../../components/configuration/deliveryTypes/DeliveryTypeDetails";
import DeliveryTypesTab from "../../components/configuration/deliveryTypes/DeliveryTypesTab";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { ActionType } from "../../state/actions";
import { AlertSeverity } from "../../constants/AlertTypes";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import { DtLabel } from "../../components/ui/label";
import { DtHeader } from '../../components/ui/header';
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

const ConfigDeliveryMethods: React.FC = () => {

    // Fetch Data
    const { saveConfigDeliveryTypesData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);

    // Delivery Type State 
    const [deliveryTypes, setDeliveryTypes] = useState<IDeliveryType[]>([]);
    const [allowMultiDeliveries, setAllowMultiDeliveries] = useState<boolean>(false);
    const [selectedDeliveryType, setSelectedDeliveryType] = useState<IDeliveryType | null>(null);
    const [selectedDeliveryTypeIdx, setSelectedDeliveryTypeIdx] = useState<string | null>(null);
    const [selectedConfigId, setSelectedConfigId] = useState<string>('');
    const [deliveryTypeTree, setDeliveryTypeTree] = useState<object[]>([]);

    // Sub States
    const [tabIndex] = useState<number>(1);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const [saving, setSaving] = useState<boolean>(false);

    // Refs
    const initialTypeSelected = useRef(false);

    // Loading Data 
    const hasFetchedData = useRef(false);
    const { data, error, loading } = useConfigurationData(ConfigurationDataType.DELIVERY_TYPES);
    const mData = useMemo(() => data, [data]);

    useEffect(() => {
        if (!hasFetchedData.current && data) {
            var validDeliveryTypes = mData?.DeliveryTypes?.map((x: IDeliveryType) => (
                { ...x })).sort((a: IDeliveryType, b: IDeliveryType) => a.Id.localeCompare(b.Id));

            nestedList(validDeliveryTypes);
            setDeliveryTypes(mData.DeliveryTypes);
            setAllowMultiDeliveries(mData.AllowMultiDelivery);
            setSelectedConfigId(EncompassService.getConfigId() as string);
            hasFetchedData.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Handling Loading Error 
    useEffect(() => {
        if (!error) {
            setErrorMessage(error);
        }
    }, [error]);

    useEffect(() => {
        if (deliveryTypes && deliveryTypes.length > 0 && !initialTypeSelected.current) {
            setInitialDeliveryType();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryTypes]);

    // Set Initial Delivery Type
    const setInitialDeliveryType = () => {
        if (deliveryTypes && deliveryTypes.length > 0) {
            const initialType = deliveryTypes.find(p => p.Enabled === true);
            if (initialType) {
                setSelectedDeliveryTypeIdx(initialType.Id);
                setSelectedDeliveryType(initialType);
                initialTypeSelected.current = true;
            }
        }
    }

    const nestedList = (data: any) => {
        let newArray: object[] = [];

        data.forEach((parent: any) => {
            let object: any = [];
            const children: any = [];

            data.forEach((child: any) => {
                object = { ...parent };
                if (child.Id.startsWith(parent.Id) && (child.Id !== parent.Id)) {
                    children.push(child);
                }

                if (children.length)
                    object.children = children;
            })
            var parentDuplicates = newArray.filter((x: any) => x.Id === object.Id).length;
            var childDuplicates = newArray.filter((n: any) => n.children?.find((m: any) => m.Id === object.Id)).length;

            if (!parentDuplicates && !childDuplicates)
                newArray.push(object);
        }, [])
        setDeliveryTypeTree(newArray);
    };

    // New Delivery Method Selected 
    const selectDeliveryType = (dtId: string) => {
        setSelectedDeliveryTypeIdx(dtId);
        setSelectedDeliveryType(deliveryTypes.find(p => p.Id === dtId) || null);
    }

    // Delivery Type Updated 
    useEffect(() => {
        if (selectedDeliveryType?.Id && deliveryTypes?.length) {
            const updatedDeliveryMethods = deliveryTypes.map(p => 
                p.Id === selectedDeliveryType.Id ? selectedDeliveryType : p
            );
            refreshLists(updatedDeliveryMethods);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDeliveryType]);

    // Delivery Type Property Changes   
    const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("Enabled", event.target.checked); };
    const handleEnabledForTpoUserChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("EnableForTpoUser", event.target.checked); };
    const handleOrderChange = (event: SelectChangeEvent) => { handleControlPropChange("Order", event.target.value); };
    const handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { handleControlPropChange("Caption", event.target.value); };
    const handleToolTipChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { handleControlPropChange("ToolTipText", event.target.value); };
    const [autoSelectEnabled, setAutoSelectEnabled] = useState(false);
    const [autoSelectExpression, setAutoSelectExpression] = useState<IExpression>({
        ExpressionName: "Auto Select",
        ExpressionString: "",
        ExpressionMessage: "",
        Enabled: false
    });

    useEffect(() => {
        if (selectedDeliveryType) {
            setAutoSelectEnabled(!!selectedDeliveryType.AutoSelectWhen);
            setAutoSelectExpression(prev => ({
                ...prev,
                ExpressionString: selectedDeliveryType.AutoSelectWhen ?? ""
            }));
        }
    }, [selectedDeliveryType]);

    const handleAutoSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setAutoSelectExpression(prev => ({
            ...prev,
            ExpressionString: value
        }));
        setAutoSelectEnabled(!!value);
        handleControlPropChange("AutoSelectWhen", value);
    };
    const handleControlPropChange = (property: keyof IDeliveryType, value: any) => {
        if (selectedDeliveryType) {
            const updatedDeliveryMethod = { ...selectedDeliveryType, [property]: value };
            setSelectedDeliveryType(updatedDeliveryMethod);
        }
    }

    const updatedSelectedDeliveryType = (updatedControl: IDeliveryType) => { 
        setSelectedDeliveryType(updatedControl);
        
        // Immediately update the deliveryTypes array to ensure persistence
        if (deliveryTypes?.length && updatedControl) {
            const updatedDeliveryMethods = deliveryTypes.map(p => 
                p.Id === updatedControl.Id ? updatedControl : p
            );
            refreshLists(updatedDeliveryMethods);
        }
    }

    // Update Delivery Type List
    const refreshLists = (updateDeliveryTypes: IDeliveryType[]) => {
        setDeliveryTypes(updateDeliveryTypes);
        nestedList([...updateDeliveryTypes].map(x => ({ ...x })).sort((a, b) => a.Id.localeCompare(b.Id)));
        saveState(updateDeliveryTypes); 
    }

    const saveState = (updatedDeliveryTypes?: IDeliveryType[]) => {
        try {
            const typesToSave = updatedDeliveryTypes || deliveryTypes;
            if (typesToSave.length === 0) return;
            const configPlugInData = store.getState().configPlugInData.data;
            const updatedConfigPlugInData = {
                ...configPlugInData,
                Configuration: {
                    ...configPlugInData?.Configuration,
                    DeliveryTypes: typesToSave,
                },
            };
            store.dispatch({
                type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                payload: updatedConfigPlugInData
            });

        } catch (e) {
            console.log("Error Saving State: ", e);
            openAlert(`Error Saving State: ${e}`, 'error');
        }
    }


    const save = async () => {
        const globalSaved = consumeGlobalSaveHadChanges();
        try {
            setSaving(true);
            const didSave = Boolean(await saveConfigDeliveryTypesData(selectedConfigId, deliveryTypes, allowMultiDeliveries, accessToken));
            const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
            const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
            const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
            const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
            const didSaveAny = didSave || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
            openAlert(didSaveAny ? "Successfully Saved Changes" : "No new changes to save", didSaveAny ? "success" : "info");
            setErrorMessage('');
        } catch (e) {
            const error = `${e}`;
            console.log("Save Error: ", error);
            setErrorMessage(error);
        } finally {
            setSaving(false);
        }
    }

    // Register save function for header buttons
    useConfigurationSave(() => save());

    function handleAllowMultiDeliveriesChange(event: ChangeEvent<HTMLInputElement>, checked: boolean): void {
        setAllowMultiDeliveries(!allowMultiDeliveries);
    }

    // Alert Handling 
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    }

    const handleListOrderChange = (event: { parent: string | null, arrangedItems: { item: string | number, Order: number }[] }) => {
        let updatedDeliveryTypes = [...deliveryTypes];

        if (event.parent === null) {
            let currentOrder = 0;

            event.arrangedItems.forEach(({ item: parentId }) => {
                const parentIndex = updatedDeliveryTypes.findIndex(dt => dt.Id === parentId);
                if (parentIndex !== -1) {
                    updatedDeliveryTypes[parentIndex] = {
                        ...updatedDeliveryTypes[parentIndex],
                        Order: currentOrder++
                    };
                }

                const children = updatedDeliveryTypes
                    .filter(dt => dt.Id.startsWith(String(parentId)) && dt.Id !== parentId)
                    .sort((a, b) => {
                        const aOrder = a.Order ?? 999999;
                        const bOrder = b.Order ?? 999999;
                        return aOrder - bOrder;
                    });

                // Assign sequential orders to children immediately after parent
                children.forEach(child => {
                    const childIndex = updatedDeliveryTypes.findIndex(dt => dt.Id === child.Id);
                    if (childIndex !== -1) {
                        updatedDeliveryTypes[childIndex] = {
                            ...updatedDeliveryTypes[childIndex],
                            Order: currentOrder++
                        };
                    }
                });
            });

        } else {
            const parentId = event.parent;

            event.arrangedItems.forEach(({ item }, index) => {
                const dtIndex = updatedDeliveryTypes.findIndex(dt => dt.Id === item);
                if (dtIndex !== -1) {
                    updatedDeliveryTypes[dtIndex] = {
                        ...updatedDeliveryTypes[dtIndex],
                        tempOrder: index
                    } as IDeliveryType & { tempOrder: number };
                }
            });

            const allParents = updatedDeliveryTypes.filter(dt => {
                // A parent is an item that is not a child of any other item
                return !updatedDeliveryTypes.some(other =>
                    other.Id !== dt.Id && dt.Id.startsWith(other.Id)
                );
            });

            const sortedParents = allParents.sort((a, b) => (a.Order ?? 999999) - (b.Order ?? 999999));

            let currentOrder = 0;

            sortedParents.forEach(parent => {
                const parentIndex = updatedDeliveryTypes.findIndex(dt => dt.Id === parent.Id);
                if (parentIndex !== -1) {
                    updatedDeliveryTypes[parentIndex] = {
                        ...updatedDeliveryTypes[parentIndex],
                        Order: currentOrder++
                    };
                }

                const children = updatedDeliveryTypes.filter(dt =>
                    dt.Id.startsWith(parent.Id) && dt.Id !== parent.Id
                );

                if (parent.Id === parentId) {
                    const sortedChildren = children.sort((a, b) => {
                        const aTempOrder = (a as any).tempOrder ?? 999999;
                        const bTempOrder = (b as any).tempOrder ?? 999999;
                        return aTempOrder - bTempOrder;
                    });

                    sortedChildren.forEach(child => {
                        const childIndex = updatedDeliveryTypes.findIndex(dt => dt.Id === child.Id);
                        if (childIndex !== -1) {
                            updatedDeliveryTypes[childIndex] = {
                                ...updatedDeliveryTypes[childIndex],
                                Order: currentOrder++
                            };
                            // Remove temp order
                            delete (updatedDeliveryTypes[childIndex] as any).tempOrder;
                        }
                    });
                } else {
                    const sortedChildren = children.sort((a, b) => (a.Order ?? 999999) - (b.Order ?? 999999));

                    sortedChildren.forEach(child => {
                        const childIndex = updatedDeliveryTypes.findIndex(dt => dt.Id === child.Id);
                        if (childIndex !== -1) {
                            updatedDeliveryTypes[childIndex] = {
                                ...updatedDeliveryTypes[childIndex],
                                Order: currentOrder++
                            };
                            // Remove temp order if it exists
                            delete (updatedDeliveryTypes[childIndex] as any).tempOrder;
                        }
                    });
                }
            });
        }

        setDeliveryTypes(updatedDeliveryTypes);
        nestedList(updatedDeliveryTypes);
        saveState();
    };

    return (
        <section>
            <div>
                {(loading || saving) && <ConfigLoad loading={loading} />}
            </div>
            <Grid container spacing={1} alignItems="stretch">
                {/* Left Side */}
                <Grid item xs={3}>
                    <DtHeader>Delivery Types</DtHeader>
                    <Divider sx={{ mt: 1 }} />
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={handleAllowMultiDeliveriesChange}
                                checked={allowMultiDeliveries}
                            />
                        }
                        label={<DtLabel size="large" color="text.primary">Allow Multi-Deliveries</DtLabel>}
                    />

                    <Divider sx={{ mb: 1 }} />
                    <DeliveryTypesList
                        deliveryTypes={deliveryTypes}
                        selectedDeliveryTypeIdx={selectedDeliveryTypeIdx}
                        selectDeliveryType={selectDeliveryType}
                        deliveryTypeTree={deliveryTypeTree}
                        onListOrderChange={handleListOrderChange}
                    />
                </Grid>
                {/* Divider */}
                <Divider orientation="vertical" flexItem sx={{ mx: 1, height: '80vh' }} />
                {/* Right Side */}
                <Grid item xs={8.6} md={8.5} lg={8.7}>
                    <DeliveryTypeDetails
                        selectedDeliveryType={selectedDeliveryType}
                        handleOnEnabledChange={handleEnabledChange}
                        handleOnEnabledForTpoUserChange={handleEnabledForTpoUserChange}
                        handleOnOrderChange={handleOrderChange} 
                        handleOnCaptionChange={handleCaptionChange}
                        handleOnToolTipTextChange={handleToolTipChange}
                        handleOnAutoSelectChange={handleAutoSelectChange}
                        autoSelect={autoSelectEnabled}
                        autoSelectExpression={autoSelectExpression}
                        setAutoSelect={setAutoSelectEnabled}
                        setAutoSelectExpression={setAutoSelectExpression}
                    />
                    <Divider sx={{ my: 0.5 }} />
                    <DeliveryTypesTab
                        tabIndex={tabIndex}
                        selectedDeliveryType={selectedDeliveryType}
                        updateParent={updatedSelectedDeliveryType}
                    />
                </Grid>
            </Grid>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertOpen}
                autoHideDuration={4000}
                onClose={handleSnackBarClose}
            >
                <MuiAlert
                    onClose={handleSnackBarClose}
                    severity={alertSeverity}
                    sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                    {alertMessage}
                </MuiAlert>
            </Snackbar>
        </section>
    )
}

export default ConfigDeliveryMethods;